import {
  translateVehicleModelFuel,
  translateVehicleModelType,
  VehicleModelWithInsuranceForD2CDto,
} from '@orcar/common';
import {
  getAvailableVehicleOptionsArray,
  getModelYearText,
  getPrimaryColor,
  JEJURO,
  VehiclePriceSummary,
} from '@orcar/common-d2c';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { optimizedImageUrl } from '@/utils/image';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '';
  const d = dayjs(value);
  return `${d.format('YYYY-MM-DD')}(${WEEKDAYS[d.day()]}) ${d.format('HH:mm')}`;
};

/**
 * 차량 옵션 = 기존 로직(getAvailableVehicleOptionsArray)이 반환하는
 * 그 차량의 실제 보유 옵션만 노출한다. (modelOption + keyOption + audio)
 */
export const getVehicleOptions = (
  vehicleModel: VehicleModelWithInsuranceForD2CDto,
): string[] => getAvailableVehicleOptionsArray(vehicleModel);

const DISCLAIMER =
  '※ 차량이미지는 이해를 돕기 위한 참고용이며, 실제 배차 차량에 따라 다를 수 있습니다.';

/** 차량 이미지 캐러셀 (자동 회전 + 좌우 버튼). */
const ImageCarousel = ({
  images,
  alt,
  isMobile,
}: {
  images: string[];
  alt: string;
  isMobile?: boolean;
}) => {
  const valid = images.filter(Boolean);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (valid.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % valid.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [valid.length]);

  const go = (delta: number) =>
    setIndex((prev) => (prev + delta + valid.length) % valid.length);

  return (
    <Thumb isMobile={isMobile}>
      {valid.length > 0 && (
        <img src={optimizedImageUrl(valid[index])} alt={alt} />
      )}
      {valid.length > 1 && (
        <>
          <NavButton
            type='button'
            side='left'
            aria-label='이전 사진'
            onClick={() => go(-1)}
          >
            ‹
          </NavButton>
          <NavButton
            type='button'
            side='right'
            aria-label='다음 사진'
            onClick={() => go(1)}
          >
            ›
          </NavButton>
          <Dots>
            {valid.map((_, i) => (
              <Dot key={i} active={i === index} />
            ))}
          </Dots>
        </>
      )}
    </Thumb>
  );
};

/** 옵션 그리드 (더보기로 접기). collapsedCount 개수까지만 보이고 나머지는 더보기. */
const CollapsibleOptions = ({
  options,
  collapsedCount,
  isTwoCol,
}: {
  options: string[];
  collapsedCount: number;
  isTwoCol?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const hasMore = options.length > collapsedCount;
  const visible = open || !hasMore ? options : options.slice(0, collapsedCount);

  return (
    <div>
      <OptionGrid isTwoCol={isTwoCol}>
        {visible.map((option) => (
          <OptionTag key={option}>{option}</OptionTag>
        ))}
      </OptionGrid>
      {hasMore && (
        <MoreButton type='button' onClick={() => setOpen((prev) => !prev)}>
          {open ? '접기' : `옵션 더보기 (${options.length - collapsedCount})`}
          <Chevron open={open} aria-hidden>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M4 6l4 4 4-4'
                stroke='currentColor'
                strokeWidth='1.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Chevron>
        </MoreButton>
      )}
    </div>
  );
};

type VehicleDetailCardProps = {
  vehiclePrice?: number;
  vehicleModel: VehicleModelWithInsuranceForD2CDto;
  isMobile?: boolean;
  /** 모바일 카드에 표시할 대여/반납 일시 */
  pickUpAt?: string | Date | null;
  dropOffAt?: string | Date | null;
};

/**
 * 결제(상세) 페이지 상단 차량 카드.
 * 데스크탑: 이미지 캐러셀 + 차명/스펙 + 옵션(더보기).
 * 모바일: 이미지 캐러셀 + 차명/스펙 + 대여·반납 일시 + 무료취소 (옵션은 하단 별도 섹션).
 */
const VehicleDetailCard = ({
  vehicleModel,
  vehiclePrice,
  isMobile,
  pickUpAt,
  dropOffAt,
}: VehicleDetailCardProps) => {
  const specPills = [
    translateVehicleModelType(vehicleModel.type),
    `${vehicleModel.seat}인승`,
    translateVehicleModelFuel(vehicleModel.fuel),
  ].filter(Boolean);

  return (
    <>
      <Card isMobile={isMobile}>
        <ImageCarousel
          images={vehicleModel.imageUrls ?? []}
          alt={vehicleModel.name}
          isMobile={isMobile}
        />
        <Info>
          <NameRow>
            <Name isMobile={isMobile}>
              {vehicleModel.title || vehicleModel.name}
            </Name>
            {specPills.map((spec) => (
              <SpecPill key={spec}>{spec}</SpecPill>
            ))}
          </NameRow>
          <SubLine>
            {getModelYearText(
              vehicleModel.minModelYear,
              vehicleModel.maxModelYear,
            )}
            {vehicleModel.driverAge
              ? ` · 만 ${vehicleModel.driverAge}세 이상`
              : ''}
          </SubLine>

          <VehiclePriceSummary
            price={vehiclePrice ?? vehicleModel.insurance.listPrice}
            referencePrice={vehicleModel.referencePrice}
            compact={isMobile}
            className='my-3'
          />

          {isMobile ? (
            <MobileDates>
              <DateLine>
                <CalendarIcon aria-hidden>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <rect
                      x='3'
                      y='4.5'
                      width='18'
                      height='16'
                      rx='3'
                      stroke='currentColor'
                      strokeWidth='1.7'
                    />
                    <path
                      d='M3 9h18M8 2.5v4M16 2.5v4'
                      stroke='currentColor'
                      strokeWidth='1.7'
                      strokeLinecap='round'
                    />
                  </svg>
                </CalendarIcon>
                {formatDateTime(pickUpAt)} ~ {formatDateTime(dropOffAt)}
              </DateLine>
            </MobileDates>
          ) : (
            <CollapsibleOptions
              options={getVehicleOptions(vehicleModel)}
              collapsedCount={12}
            />
          )}
        </Info>
      </Card>
      {!isMobile && <Disclaimer>{DISCLAIMER}</Disclaimer>}
    </>
  );
};

export { VehicleDetailCard };

/** 모바일 전용 "차량 옵션" 별도 섹션 (대여 유의사항 위에 배치). */
const VehicleOptionSection = ({
  vehicleModel,
}: {
  vehicleModel: VehicleModelWithInsuranceForD2CDto;
}) => (
  <OptionSectionBox>
    <OptionSectionTitle>차량 옵션</OptionSectionTitle>
    <CollapsibleOptions
      options={getVehicleOptions(vehicleModel)}
      collapsedCount={6}
      isTwoCol
    />
    <Disclaimer>{DISCLAIMER}</Disclaimer>
  </OptionSectionBox>
);

export { VehicleOptionSection };

const Card = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: ${({ isMobile }) => (isMobile ? '16px' : '28px')};
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'flex-start')};
  padding: ${({ isMobile }) => (isMobile ? '18px 0' : '24px 0')};
`;

const Thumb = styled.div<{ isMobile?: boolean }>`
  position: relative;
  flex: 0 0 ${({ isMobile }) => (isMobile ? '140px' : '300px')};
  width: ${({ isMobile }) => (isMobile ? '140px' : '300px')};
  /* 차량 사진과 같은 3:2 비율 — contain 레터박싱으로 생기던 여백을 없앤다. */
  height: ${({ isMobile }) => (isMobile ? '94px' : '200px')};
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(
    150deg,
    rgba(0, 145, 232, 0.08),
    ${({ theme }) => theme.COLOR.GREY_BG}
  );
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &:hover button {
    opacity: 1;
  }
`;

const NavButton = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ side }) => (side === 'left' ? 'left: 6px;' : 'right: 6px;')}
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  color: ${({ theme }) => theme.COLOR.GREY_01};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
  box-shadow: 0 2px 6px rgba(15, 27, 36, 0.15);
`;

const Dots = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
`;

const Dot = styled.span<{ active?: boolean }>`
  width: ${({ active }) => (active ? '16px' : '6px')};
  height: 6px;
  border-radius: 999px;
  background: ${({ active, theme }) =>
    active ? getPrimaryColor(JEJURO) : theme.COLOR.GREY_04};
  transition: all 0.2s ease;
`;

const Info = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Name = styled.h3<{ isMobile?: boolean }>`
  margin: 0;
  ${({ isMobile, theme }) => (isMobile ? theme.FONT.H4 : theme.FONT.H3)};
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const SpecPill = styled.span`
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: 999px;
  padding: 3px 10px;
`;

const SubLine = styled.p`
  margin: 0 0 4px;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const MobileDates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const DateLine = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_01};
  word-break: keep-all;
`;

const CalendarIcon = styled.span`
  display: inline-flex;
  flex: none;
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;

/* 옵션명 길이가 제각각이라 고정 그리드 대신 내용 너비만큼의 칩으로 흐르게 한다.
   → 이름이 길어도 세로로 뚱뚱해지지 않고 가로로 넓어지며 자연스럽게 줄바꿈된다. */
const OptionGrid = styled.div<{ isTwoCol?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const OptionTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 14px;
  white-space: nowrap;
  ${({ theme }) => theme.FONT.B3};
  border-radius: 10px;
  color: ${getPrimaryColor(JEJURO)};
  border: 1px solid ${getPrimaryColor(JEJURO)}44;
  background: ${getPrimaryColor(JEJURO)}0d;
  font-weight: 600;
`;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 12px;
  padding: 12px 0;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: 10px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  color: ${({ theme }) => theme.COLOR.GREY_01};
  ${({ theme }) => theme.FONT.B2};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.COLOR.GREY_BG};
  }
`;

const Chevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.COLOR.GREY_02};
  transition: transform 0.2s ease;
  ${({ open }) => open && 'transform: rotate(180deg);'}
`;

const Disclaimer = styled.p`
  margin: 0;
  padding-bottom: 16px;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;

const OptionSectionBox = styled.div`
  width: 100%;
  padding: 20px 0;
`;

const OptionSectionTitle = styled.p`
  margin-bottom: 16px;
  ${({ theme }) => theme.NEW_FONT.H4};
  font-weight: 700;
`;
